import MobileNav from '@ux/mobile-nav';
import { TrayMenu } from '@ux/tray-menu';
import { golf } from './enums';
import Link from '@ux/link';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import { injectIntl } from 'react-intl';
import React from 'react';
import PropTypes from 'prop-types';
import { getCurrentHref } from './util';
import { url } from '@ux/header-util';

const isDataAttr = /^data-[a-zA-Z0-9-]+$/;
const isRelativeURL = /^\/(?:[^/]|$)/;
const absoluteURLPrefix = /^https?:\/\/[^/]*/;

class Navigation extends NamespaceConsumer {
  constructor(props) {
    super(...arguments);

    this.currentHref = getCurrentHref(props.currentHref, this.isBrowser, this.getDocumentLocation());
    this.renderItems = this.renderItems.bind(this);
    this.closeNavTrays = this.closeNavTrays.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.trayMenus = Object.create(null);
  }

  static getDerivedStateFromProps(props) {
    return {
      items: props.items.map(Navigation.setDataAttrs)
    };
  }

  getDocumentLocation() {
    if (typeof window !== 'undefined') {
      return document && document.location && document.location.href;
    }
    return null;
  }

  closeNavTrays() {
    Object.values(this.trayMenus).forEach(m => m.close && m.close());
  }

  onItemClick(item, event) {
    if (item.onClick) {
      item.onClick(event);
    }
    this.closeNavTrays();
  }

  static setDataAttrs(item) {
    if (!item) return;

    item.dataAttrs = Object.keys(item).reduce(function (acc, k) {
      if (isDataAttr.test(k)) { acc[k] = item[k]; }
      return acc;
    }, {});

    if (item.children) {
      item.children.forEach(Navigation.setDataAttrs);
    }

    return item;
  }

  isActive(item, recurse) {
    if (typeof item.active === 'boolean') return item.active;

    if (item.href && item.href === this.currentHref) return true;

    // Handle relative URLs
    if (this.currentHref && isRelativeURL.test(item.href) && item.href === this.currentHref.replace(absoluteURLPrefix, '')) {
      return true;
    }

    if (recurse !== false && item.children) {
      return item.children.some(child => this.isActive(child));
    }

    return false;
  }

  renderMobile(items) {

    const props = this.props;
    const mobileItems = [...items];
    let activeHeading;
    const loginCaption = props.intl.formatMessage({ id: golf.login });

    for (let i = 0; i < mobileItems.length; i++) {
      if (this.isActive(mobileItems[i])) {
        activeHeading = mobileItems[i];
        break;
      }
    }

    if (!props.loggedIn) {
      let loginUrl = '';
      if (props.urls && props.urls.sso) {
        loginUrl = props.urls.sso.login && url.updateQuery(props.urls.sso.login.href, props.sso);
      }
      mobileItems.push({
        caption: loginCaption,
        href: loginUrl
      });
    }

    return (
      <MobileNav
        activeHeading={ activeHeading }
        currentHref={ this.currentHref }
        literalCaptions={ true }
        market={ props.market }
        menuTitle={ props.intl.formatMessage({ id: golf.helpCenter }) }
        messages={ props.intl.messages }
        nav={ mobileItems }
        showButtons={ false }
        showHomeLink={ false }
        showMenuTitleOnHome={ true }
        urls={ props.urls }
        useActiveHeadingAsMenuTitle={ true }
        useGradientHighlightBar={ true }
      />
    );
  }

  renderChild(item, key) {
    if (item.mobileOnly && !this.props.renderMobile) {
      return null;
    }
    const classNames = this.classNames(item.className,
      this.namespace(this.classNames({
        active: this.isActive(item, false)
      })));
    const props = {
      'className': classNames,
      'target': item.target,
      'id': item.id,
      'href': item.href || '#',
      'data-eid': item.eid,
      'onClick': (e => { this.onItemClick(item, e); }),
      'onClicked': this.closeNavTrays,
      ...item.dataAttrs
    };
    return <li key={ key } className={ props.className }>
      <Link { ...props }>
        { item.caption }
      </Link>
    </li>;
  }

  renderItems(item, i) {

    if (item.mobileOnly && !this.props.renderMobile) {
      return null;
    }

    const isActive = this.isActive(item);
    const classNames = this.namespace(this.classNames('nav-item', {
      active: isActive
    }));
    const highlightClassNames = this.namespace(this.classNames('nav-item-highlight', {
      active: isActive
    }));

    if (item.children && item.children.length) {

      const childrenItems = <ul
        className={ this.namespace('list-unstyled', 'nav-child') }
        { ...item.dataAttrs }>
        { item.children.map((child, k) => {
          return this.renderChild(child, k);
        })}
      </ul>;

      const trayTitle = <div className={ this.namespace('tray-title', 'help-tray-title') }>
        {item.caption}
      </div>;

      return <li className={ classNames } key={ i }>
        <TrayMenu
          ref={ r => { this.trayMenus[i] = r; } }
          name={ trayTitle }
          data-eid={ item.eid }
          fullwidth={ false }
          showClose={ false }
          { ...item.dataAttrs }>
          { childrenItems }
        </TrayMenu>
        <div className={ highlightClassNames } role='presentation'>&nbsp;</div>
      </li>;
    }

    return (
      <li key={ i } className={ classNames }>
        <a
          className={ this.namespace('nav-link', 'font-base') }
          id={ item.id }
          href={ item.href }
          target={ item.target }
          onClick={ item.onClick }
          data-eid={ item.eid }
          { ...item.dataAttrs }
        >
          { item.caption }
        </a>
        <div className={ highlightClassNames } role='presentation'>&nbsp;</div>
      </li>
    );
  }

  render() {
    const props = this.props;
    const { items } = props;

    if (!items.length) {
      return null;
    }

    if (props.renderMobile) {
      return this.renderMobile(items);
    }

    return (
      <ul className={ this.namespace('nav', `nav-left`) }>
        { items.map(this.renderItems) }
      </ul>
    );
  }
}

Navigation.defaultProps = {
  items: []
};

Navigation.propTypes = {
  ...NamespaceShape,
  items: PropTypes.array,
  renderMobile: PropTypes.bool,
  urls: PropTypes.object.isRequired,
  loggedIn: PropTypes.bool,
  sso: PropTypes.object
};

export default withNamespace(injectIntl(Navigation));

